import { Box, IconButton, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import StatusBar from '@/designs-dev/device/StatusBar';
import KeniOrb from '@/components/wrapped/KeniOrb';
import BenefitRow from '@/north-star/BenefitRow';
import { REVEAL } from '@/north-star/NorthStar.content';
import {
  CLAIM_TOTAL_ANNUAL,
  FOUND_BENEFITS,
} from '@/north-star/NorthStar.fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// The "aha": based on the chat, here's what Mum could claim. Presented as a
// modal, so it has a close in the corner as well as the primary Next.
export type RevealScreenProps = {
  onContinue: () => void;
  onClose: () => void;
  /** Reopen the Keni conversation. The orb in the footer is the entry point. */
  onAskKeni: () => void;
};

const RevealScreen = ({
  onContinue,
  onClose,
  onAskKeni,
}: RevealScreenProps) => (
  <ScreenRoot>
    <StatusBar />

    {/* modal close, top-right */}
    <Stack sx={{ justifyContent: 'flex-end', px: 2, py: 1 }} direction='row'>
      <IconButton aria-label='Close' onClick={onClose}>
        <Icon icon={'xmark' as never} fontSize={18} />
      </IconButton>
    </Stack>

    <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 1, pb: '56px' }}>
      <Stack spacing={3}>
        <Typography sx={{ textAlign: 'center' }} variant='h2'>
          {REVEAL.heading}
        </Typography>

        {/* what they could claim */}
        <Stack
          sx={{
            p: 3,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'custom.neutral.200',
            borderRadius: `${radius.xl}px`,
            alignItems: 'center',
            textAlign: 'center',
          }}
          spacing={0.5}
        >
          <Typography variant='body2' color='text.secondary'>
            {REVEAL.claimLead}
          </Typography>
          <Typography variant='h1' color='custom.coral.700'>
            {CLAIM_TOTAL_ANNUAL}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {REVEAL.claimPerYear}
          </Typography>
        </Stack>

        <Stack spacing={1.5}>
          {FOUND_BENEFITS.map((b) => (
            <BenefitRow key={b.name} benefit={b} />
          ))}
        </Stack>
      </Stack>
    </Box>

    <Box
      sx={{
        position: 'relative',
        px: 3,
        pb: 2,
        borderTop: 1,
        borderColor: 'custom.neutral.200',
        bgcolor: 'background.paper',
        pt: '52px',
      }}
    >
      {/* orb straddles the top edge — half on the white — tap to reopen Keni */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Box
          component='button'
          onClick={onAskKeni}
          aria-label='Ask Keni'
          sx={{
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: 0,
          }}
        >
          <KeniOrb size={80} state='speaking' />
        </Box>
      </Box>
      <Button
        variant='primary'
        size='lg'
        isFullWidth
        ariaLabel={REVEAL.cta}
        onPress={onContinue}
      >
        {REVEAL.cta}
      </Button>
    </Box>
  </ScreenRoot>
);

RevealScreen.displayName = 'RevealScreen';
export default RevealScreen;
